import { PropertyResponseDto } from "@/common/api/datasources/Api";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { format, formatDistanceToNow } from "date-fns";
import { AlertCircle, Calendar, Clock, History } from "lucide-react";
import { useTranslation } from "react-i18next";

interface PropertyDatesProps {
    property: PropertyResponseDto;
}

export const PropertyDates = ({ property }: PropertyDatesProps) => {
    const { t } = useTranslation();

    const getStatusChangeIndicator = () => {
        if (!property.lastStatusChange) return null;

        const daysFromLastChange = Math.abs(
            Math.floor(
                (new Date().getTime() - new Date(property.lastStatusChange).getTime()) /
                (1000 * 60 * 60 * 24)
            )
        );

        let color = "bg-green-500";
        if (daysFromLastChange > 30) color = "bg-yellow-500";
        if (daysFromLastChange > 90) color = "bg-red-500";

        return (
            <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${color} animate-pulse`} />
                <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(property.lastStatusChange), { addSuffix: true })}
                </span>
            </div>
        );
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg font-medium flex items-center gap-2">
                    <History className="h-5 w-5 text-primary" />
                    {t("properties.details.dates")}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    <TooltipProvider>
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                                <div className="flex items-center gap-3">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">{t("properties.details.created")}</span>
                                </div>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <Badge variant="secondary" className="font-medium">
                                            {format(new Date(property.createdAt), "PPP")}
                                        </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{format(new Date(property.createdAt), "PPP p")}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatDistanceToNow(new Date(property.createdAt), { addSuffix: true })}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>

                            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                                <div className="flex items-center gap-3">
                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">{t("properties.details.updated")}</span>
                                </div>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <Badge variant="secondary" className="font-medium">
                                            {format(new Date(property.updatedAt), "PPP")}
                                        </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{format(new Date(property.updatedAt), "PPP p")}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatDistanceToNow(new Date(property.updatedAt), { addSuffix: true })}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>

                            {property.lastStatusChange && (
                                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                                    <div className="flex items-center gap-3">
                                        <AlertCircle className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-sm text-muted-foreground">
                                            {t("properties.details.statusChanged")}
                                        </span>
                                    </div>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <div className="flex items-center gap-2">
                                                <Badge variant="secondary" className="font-medium">
                                                    {format(new Date(property.lastStatusChange), "PPP")}
                                                </Badge>
                                                {getStatusChangeIndicator()}
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{format(new Date(property.lastStatusChange), "PPP p")}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {formatDistanceToNow(new Date(property.lastStatusChange), { addSuffix: true })}
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    </TooltipProvider>
                </div>
            </CardContent>
        </Card>
    );
}; 